import { QUICK_SELECTS, QuickSelect } from '@/components/types'
import { quickSelectToTimeBefore } from '@/utils/calendar-utils'
import { CalendarFilters, useCurrentFeatureFilters } from '@/composables/useFilters'
import {
  formatDifferentYears,
  formatSameYear,
  formatSameYearSingleDay
} from '@/utils/calendar-utils/formatters'
import { isSameDay, isSameYear } from 'date-fns'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const useButtonTextFormatter = (locale: Locale) => {
  const dashboard = useCurrentFeatureFilters()
  const buttonText = ref<string>('')
  const i18n = useI18n()

  function formatAssociatedHours(onlyBusinessDays?: boolean, onlyBusinessHours?: boolean) {
    const substrings: string[] = []
    if (onlyBusinessHours) {
      substrings.push(i18n.t('general__text__businesshours'))
    }
    if (onlyBusinessDays) {
      substrings.push(i18n.t('general__text__businessdays'))
    }
    if (substrings.length === 1) {
      return `(${substrings[0]})`
    }
    if (substrings.length === 2) {
      return `(${substrings[0]}, ${substrings[1]})`
    }
    return ''
  }

  function buildDateParts(from: Date, to: Date): string[] {
    const parts: string[] = []
    if (!isSameYear(from, to)) {
      parts.push(formatDifferentYears(from, to, locale))
    } else if (isSameDay(from, to)) {
      parts.push(formatSameYearSingleDay(from, locale))
    } else {
      parts.push(formatSameYear(from, to, locale))
    }
    return parts
  }

  function setButtonText(
    calendar: CalendarFilters,
    onlyBusinessDays?: boolean,
    onlyBusinessHours?: boolean
  ) {
    let substrings: string[] = []

    if (!calendar.from && !calendar.to && !calendar.quickSelect) {
      throw new Error('No calendar filters provided - we should not be here!')
    }

    if (calendar.from && calendar.to) {
      const assocativeHours = formatAssociatedHours(onlyBusinessDays, onlyBusinessHours)
      substrings = [
        ...buildDateParts(new Date(calendar.from), new Date(calendar.to)),
        assocativeHours
      ]
    }

    if (calendar.quickSelect) {
      const match = QUICK_SELECTS.find((x) => x.name === calendar.quickSelect)
      const { from, to } = quickSelectToTimeBefore(new Date(), match as QuickSelect)
      const assocativeHours = formatAssociatedHours(onlyBusinessDays, onlyBusinessHours)
      substrings = [...buildDateParts(new Date(from), new Date(to)), assocativeHours]
    }

    buttonText.value = substrings.join(' ')
  }

  watch(
    () => dashboard.value?.current,
    (update) => {
      if (update && 'calendar' in update && update.calendar?.from && update.calendar.to) {
        setButtonText(
          update.calendar,
          'onlyBusinessDays' in update && update.onlyBusinessDays,
          'onlyBusinessHours' in update && update.onlyBusinessHours
        )
      } else {
        buttonText.value = ''
      }
    },
    {
      deep: true,
      immediate: true
    }
  )

  return {
    buttonText
  }
}

export default useButtonTextFormatter
